import { Grid } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useAlert } from 'shared/components/alerts';
import { FormProvider, useForm } from 'react-hook-form';
import { RenderUnsettledUI } from 'shared/components';
import { Header, Page } from 'redesign';
import { paths } from 'ogp/paths';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'Routes';
import { ProjectsUpdatePayload } from 'ogp/services/project-service.types';
import { invariant } from 'shared/utils/utils';
import { ProjectForm } from 'shared/components/project-forms/components/project-form';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import {
  ProjectFormTypeAll,
  ProjectFormTypeDuplicate,
} from 'shared/components/project-forms/types/project-form.types';
import { mapProjectToDetails } from 'api/services/work-template/work-template.utils';
import { ProjectDetail } from 'api/services/project/project-service.schema';
import { DateTime } from 'luxon';
import { useExternalParams } from 'shared/hooks';
import { stringifyFilters } from 'shared/utils/stringify-filters';
import { useUpdateProject } from '../../hooks/use-update-project';
import { useGetProjectDetail } from '../../../../../../../shared/components/planner/project-detail-modal/detail/applications/hooks/use-get-project-detail';
import { PROJECT_PLANNING_INITIAL_VALUES } from '../../filters/hooks/use-project-planning-filters';

export const ProjectUpdateDataProvider = () => {
  const { id: projectId } = useParams<'id'>();
  invariant(projectId, 'Project ID should be defined');
  const projectQuery = useGetProjectDetail(projectId);

  if (projectQuery.status !== 'success') {
    return <RenderUnsettledUI data={projectQuery} />;
  }

  return <ProjectsUpdate projectData={projectQuery.data} />;
};

type ProjectUpdateProps = {
  projectData: ProjectDetail;
};

const ProjectsUpdate = ({ projectData }: ProjectUpdateProps) => {
  const { alertSuccess, alertError } = useAlert();
  const [params] = useExternalParams(PROJECT_PLANNING_INITIAL_VALUES);
  const formattedParams = stringifyFilters({
    ...params,
    toDate: params.toDate
      ? params.toDate.toFormat('yyyy-MM-dd')
      : DateTime.now().toFormat('yyyy-MM-dd'),
  });
  const formMethods = useForm<ProjectFormTypeDuplicate>({
    defaultValues: {
      departmentId: projectData.department.id,
      startDate: projectData.startDate,
      endDate: projectData.endDate,
      hourlyRate: (projectData.freelanceProperties?.hourlyRateCents ?? 0) / 100,
      weeklyHours: projectData.weekHourVolume,
      isTempwork: !!projectData.tempWorkProperties,
      isFreelance: !!projectData.freelanceProperties,
      location: projectData.location ?? null,
      isFreelanceOrtApplicable: projectData.freelanceProperties?.ortApplicable ?? false,
      isTempworkOrtApplicable: projectData.tempWorkProperties?.ortApplicable ?? false,
    },
  });
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const mutation = useUpdateProject();
  const isProjectWithActivePlacement = projectData.placements.some(
    (placement) => placement.state === 'active'
  );

  const handleSubmit = () => {
    formMethods.handleSubmit((data: ProjectFormTypeAll) => {
      invariant(data.location, 'Location is required');

      const payload: ProjectsUpdatePayload = {
        departmentId: data.departmentId,
        id: projectData.id,
        startDate: data.startDate,
        endDate: data.endDate,
        weekHourVolume: +data.weeklyHours,
        freelanceProperties: data.isFreelance
          ? {
              hourlyRateCents: Math.round(+data.hourlyRate * 100),
              ortApplicable: data.isFreelanceOrtApplicable ?? false,
            }
          : undefined,
        tempWorkProperties: data.isTempwork
          ? {
              ortApplicable: data.isTempworkOrtApplicable ?? false,
            }
          : undefined,
        location: data.location,
      };
      mutation.mutate(payload, {
        onSuccess: () => {
          alertSuccess('Project geüpdatet');
          navigate(generatePath(routes.Projects, {}, formattedParams));
        },
        onError: (e) => alertError(e),
      });
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText="Update project" />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            mode={'update'}
            onCancel={() =>
              navigate(generatePath(paths.Projects, {}, formattedParams), { replace: true })
            }
          />
        }
      >
        <Grid container>
          <Grid item xs={12}>
            <ProjectForm
              isProjectWithActivePlacement={isProjectWithActivePlacement}
              mode="update"
              workDetails={mapProjectToDetails(projectData)}
            />
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
